import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Col, Form, Row } from "reactstrap";

import "@/assets/scss/modern-modal.css";
import { AsyncSelect, DatePickerField, EmailInputField, InputField, MobileInputField, NameInputField } from "@/components/Common/Inputs";
import NormalSelect from "@/components/Common/Inputs/NormalSelect";
import InfoRow from "@/components/Common/Inforow";
import MissingEmailModal from "@/components/Common/Modals/MissingEmailModal";
import TransactionDetailsModal from "@/components/Common/Modals/TransactionDetailsModal";
import Section from "@/components/Common/Section";
import TransactionFilesList from "@/components/Common/TransactionFilesList";
import HistoryList from "@/components/Common/HistoryList";
import useCustomHook from "@/components/Hooks/useCustomHook";
import { GLOBO_ACCESS_RIGHTS } from "@/constants/AccessRights";
import { useNotificationModal } from "@/context/notificationContext";
import { assertApiSuccess } from "@/helpers/api_helper";
import { buildPayload, buildSelectPairs, flattenSelectPairs, formatLoadOptions } from "@/helpers/data_helper";
import { formatDate } from "@/helpers/date_helper";
import { hasWriteAccess } from "@/helpers/session_helper";

import ActionModal from "./ActionModal";
import SendEmailModal from "./SendEmailModal";

import { useExportPatientEnrollmentLogsMutation, useGetPatientEnrollmentLogsQuery } from "@/api/Endpoints/Globo/Transactions/PatientEnrollment/PatientEnrollmentLogs";
import {
  useFindPatientEnrollmentsQuery,
  useGetFilesPatientEnrollmentsQuery,
  useSavePatientEnrollmentsMutation,
} from "@/api/Endpoints/Globo/Transactions/PatientEnrollment/PatientEnrollments";
import { useLookUpCitiesMutation } from "@/api/Endpoints/Master/StaticData/Cities";
import { useLookUpProvincesMutation } from "@/api/Endpoints/Master/StaticData/Provinces";
import { useLookUpDoctorsMutation } from "@/api/Endpoints/Globo/Doctors/Doctors";
import { useLookUpDiagnosisMutation } from "@/api/Endpoints/Globo/StaticData/Diagnosis";
import { useLookUpHospitalsMutation } from "@/api/Endpoints/Globo/StaticData/Hospitals";
import { useLookUpPatientSegmentsMutation } from "@/api/Endpoints/Globo/StaticData/PatientSegments";
import { useLookUpTherapeuticAreasMutation } from "@/api/Endpoints/Globo/StaticData/TherapeuticAreas";

const MODULE_NAME = "Patient Enrollment";

function RequestDetailsViewMode({ data }) {
  const fullName = [data?.firstName, data?.middleName, data?.lastName, data?.suffix].filter(Boolean).join(" ");
  const currentAddress = [data?.streetAddress, data?.barangay, data?.cityName, data?.provinceName].filter(Boolean).join(", ");

  return (
    <div style={{ paddingTop: 4 }}>
      <Section title="Patient Information">
        <Row>
          <Col lg={12}>
            <InfoRow icon="bx bx-cog" label="Program Name" value={data?.programName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-user" label="Full Name" value={fullName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-calendar" label="Birth Date" value={formatDate(data?.birthDate)} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-id-card" label="Gender" value={data?.gender} />
          </Col>
          <Col lg={6}>
            <InfoRow isEmail icon="bx bx-envelope" label="Email Address" value={data?.emailAddress} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-phone-call" label="Mobile No." value={data?.mobileNo} />
          </Col>
        </Row>
      </Section>

      <Section title="Address">
        <Row>
          <Col lg={12}>
            <InfoRow icon="bx bx-map" label="Current Address" value={currentAddress} />
          </Col>
        </Row>
      </Section>

      <Section title="Emergency Contact">
        <Row>
          <Col lg={6}>
            <InfoRow icon="bx bx-group" label="Contact Person" value={data?.secondaryContactName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-heart" label="Relationship" value={data?.secondaryRelationshipToPatient} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-phone-call" label="Contact No." value={data?.secondaryContactNo} />
          </Col>
        </Row>
      </Section>

      <Section title="Medical Information">
        <Row>
          <Col lg={6}>
            <InfoRow icon="bx bx-heart-circle" label="Therapeutic Area" value={data?.therapeuticAreaName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-user-check" label="Doctor" value={data?.doctorName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-plus-medical" label="Hospital" value={data?.hospitalName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-clipboard" label="Diagnosis" value={data?.diagnosisName} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-injection" label="Treatment Line" value={data?.treatmentLine} />
          </Col>
          <Col lg={6}>
            <InfoRow icon="bx bx-group" label="Patient Segment" value={data?.patientSegmentName} />
          </Col>
        </Row>
      </Section>

      <Section title="Remarks">
        <Row>
          <Col lg={12}>
            <InfoRow icon="bx bx-message-square-detail" label="Remarks" value={data?.remarks} />
          </Col>
        </Row>
      </Section>

      <Section title="Status Details">
        <Row>
          <Col lg={6}>
            <InfoRow label="Date Submitted" value={formatDate(data.dateSubmitted)} icon="ri-calendar-2-line" />
          </Col>
          <Col lg={6}>
            <InfoRow label="Submitted By" value={data.submittedByName} icon="ri-user-3-line" />
          </Col>
          <Col lg={6}>
            <InfoRow label="Date Approved" value={formatDate(data.dateApproved)} icon="ri-calendar-2-line" />
          </Col>
          <Col lg={6}>
            <InfoRow label="Approved By" value={data.approvedByName} icon="ri-user-3-line" />
          </Col>
        </Row>
      </Section>
    </div>
  );
}

function RequestDetailsEditMode({ data, registerSubmit, onSetSaving, onSaveSuccess }) {
  const { notification } = useNotificationModal();
  const [save, { isLoading: isSaving }] = useSavePatientEnrollmentsMutation();

  const [lookupTherapeuticAreas] = useLookUpTherapeuticAreasMutation();
  const [lookupDoctors] = useLookUpDoctorsMutation();
  const [lookupHospitals] = useLookUpHospitalsMutation();
  const [lookupDiagnoses] = useLookUpDiagnosisMutation();
  const [lookupPatientSegments] = useLookUpPatientSegmentsMutation();
  const [lookUpProvince] = useLookUpProvincesMutation();
  const [lookUpCity] = useLookUpCitiesMutation();

  const defaultValues = {
    patientEnrollmentId: data?.patientEnrollmentId || 0,
    firstName: data?.firstName || "",
    middleName: data?.middleName || "",
    lastName: data?.lastName || "",
    suffix: data?.suffix || "",
    emailAddress: data?.emailAddress || "",
    mobileNo: data?.mobileNo || "",
    birthDate: data?.birthDate || null,
    gender: data?.gender || "",
    streetAddress: data?.streetAddress || "",
    barangay: data?.barangay || "",
    cityId: data?.cityId || 0,
    cityName: data?.cityName || "",
    provinceId: data?.provinceId || 0,
    provinceName: data?.provinceName || "",
    secondaryContactName: data?.secondaryContactName || "",
    secondaryContactNo: data?.secondaryContactNo || "",
    secondaryRelationshipToPatient: data?.secondaryRelationshipToPatient || "",
    therapeuticAreaId: data?.therapeuticAreaId || 0,
    doctorId: data?.doctorId || 0,
    hospitalId: data?.hospitalId || 0,
    diagnosisId: data?.diagnosisId || 0,
    treatmentLine: data?.treatmentLine || "",
    patientSegmentId: data?.patientSegmentId || 0,
    programId: data?.programId || 0,
    programName: data?.programName || "",
    principalId: data?.principalId || 0,
    remarks: data?.remarks || "",
    files: data?.files || [],
  };

  const { handleSubmit, reset, control, watch, setValue } = useForm({ defaultValues });

  useEffect(() => {
    reset({
      ...defaultValues,
      ...buildSelectPairs(
        ["therapeuticAreaId", "doctorId", "hospitalId", "diagnosisId", "patientSegmentId", "cityId", "provinceId"],
        data,
      ),
      secondaryRelationshipToPatient: data?.secondaryRelationshipToPatient
        ? { value: data.secondaryRelationshipToPatient, label: data.secondaryRelationshipToPatient }
        : null,
      gender: data?.gender ? { value: data.gender, label: data.gender } : null,
    });
  }, [data]);

  const onSubmit = async (formData) => {
    const payload = {
      ...buildPayload(defaultValues, formData),
      ...flattenSelectPairs(
        ["therapeuticAreaId", "doctorId", "hospitalId", "diagnosisId", "patientSegmentId", "cityId", "provinceId"],
        formData,
      ),
      secondaryRelationshipToPatient: formData?.secondaryRelationshipToPatient?.value || "",
      gender: formData?.gender?.value,
    };

    try {
      const response = await save(payload).unwrap();
      assertApiSuccess(response);
      notification({
        type: "success",
        title: response?.returnData?.referenceNo || MODULE_NAME,
        header: `Update ${MODULE_NAME}`,
        message: `${MODULE_NAME} was successfully updated.`,
      });
      onSaveSuccess?.();
    } catch (error) {
      notification({ type: "error", title: MODULE_NAME, message: `${error?.message || "Unknown error"}` });
    }
  };

  const provinceId = watch("provinceId")?.value || 0;

  useEffect(() => {
    registerSubmit?.(() => handleSubmit(onSubmit)());
    return () => registerSubmit?.(null);
  }, [handleSubmit, data]);

  useEffect(() => {
    onSetSaving?.(isSaving);
  }, [isSaving]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)} style={{ paddingTop: 4 }}>
      <Section title="Patient Information">
        <Row>
          <Col lg={3}>
            <NameInputField
              name="firstName"
              control={control}
              label="First Name"
              type="text"
              maxLength={30}
              rules={{
                required: "First name is required.",
                maxLength: { value: 30, message: "First name must not exceed 30 characters" },
              }}
              placeholder="Enter first name..."
            />
          </Col>
          <Col lg={3}>
            <NameInputField
              name="middleName"
              control={control}
              label="Middle Name"
              type="text"
              maxLength={30}
              placeholder="Enter middle name..."
            />
          </Col>
          <Col lg={3}>
            <NameInputField
              name="lastName"
              control={control}
              label="Last Name"
              type="text"
              maxLength={30}
              rules={{
                required: "Last name is required.",
                maxLength: { value: 30, message: "Last name must not exceed 30 characters" },
              }}
              placeholder="Enter last name..."
            />
          </Col>
          <Col lg={3}>
            <NameInputField name="suffix" control={control} label="Suffix" type="text" maxLength={4} placeholder="Jr., Sr., III..." />
          </Col>
          <Col lg={3}>
            <DatePickerField
              control={control}
              name="birthDate"
              label="Birth Date"
              options={{ maxDate: new Date() }}
              placeholder="Select birth date..."
            />
          </Col>
          <Col lg={3}>
            <NormalSelect
              options={[
                { value: "MALE", label: "MALE" },
                { value: "FEMALE", label: "FEMALE" },
              ]}
              control={control}
              name="gender"
              label="Gender"
              placeholder="Select gender..."
              rules={{ required: "Gender is required." }}
            />
          </Col>
          <Col lg={3}>
            <EmailInputField
              name="emailAddress"
              control={control}
              label="Email Address"
              rules={{ required: "Email address is required." }}
              placeholder="Enter email address..."
            />
          </Col>
          <Col lg={3}>
            <MobileInputField
              name="mobileNo"
              control={control}
              label="Mobile No."
              rules={{ required: "Mobile no is required." }}
              placeholder="Enter mobile no..."
            />
          </Col>
          <Col lg={3}>
            <InputField
              name="secondaryContactName"
              control={control}
              label="Secondary Contact Name"
              type="text"
              maxLength={100}
              placeholder="Enter secondary contact name..."
            />
          </Col>
          <Col lg={3}>
            <MobileInputField
              name="secondaryContactNo"
              control={control}
              label="Secondary Contact No."
              placeholder="Enter secondary contact no..."
            />
          </Col>
          <Col lg={3}>
            <NormalSelect
              options={[
                { value: "Husband / Wife", label: "Husband / Wife" },
                { value: "Parent", label: "Parent" },
                { value: "Guardian", label: "Guardian" },
                { value: "Sibling", label: "Sibling" },
                { value: "Friend", label: "Friend" },
                { value: "Others", label: "Others" },
              ]}
              control={control}
              name="secondaryRelationshipToPatient"
              label="Relationship To Patient"
              placeholder="Enter relationship to patient..."
            />
          </Col>
        </Row>
      </Section>

      <Section title="Address Information">
        <Row>
          <Col lg={4}>
            <AsyncSelect
              name="provinceId"
              control={control}
              label="Province"
              loadOptions={formatLoadOptions(lookUpProvince)}
              placeholder="Select province..."
              getOptionValue={(opt) => opt.provinceId || opt.value}
              getOptionLabel={(opt) => opt.provinceName || opt.label}
              onChange={(selected) => {
                setValue("provinceId", selected);
                setValue("cityId", null);
              }}
            />
          </Col>
          <Col lg={4}>
            <AsyncSelect
              key={provinceId}
              isDisabled={!provinceId}
              name="cityId"
              control={control}
              label="City"
              loadOptions={formatLoadOptions(lookUpCity, { provinceId })}
              placeholder="Select city..."
              getOptionValue={(opt) => opt.cityId || opt.value}
              getOptionLabel={(opt) => opt.cityName || opt.label}
            />
          </Col>
          <Col lg={4}>
            <InputField name="barangay" control={control} label="Barangay" type="text" maxLength={100} placeholder="Enter barangay..." />
          </Col>
          <Col lg={12}>
            <InputField
              name="streetAddress"
              control={control}
              label="Street Address"
              type="text"
              maxLength={150}
              placeholder="Enter street address..."
            />
          </Col>
        </Row>
      </Section>

      <Section title="Medical Information">
        <Row>
          <Col lg={4}>
            <AsyncSelect
              loadOptions={formatLoadOptions(lookupDoctors)}
              name="doctorId"
              control={control}
              label="Doctor"
              rules={{ required: "Doctor is required." }}
              placeholder="Select doctor..."
              getOptionValue={(opt) => opt.doctorId || opt.value}
              getOptionLabel={(opt) => opt.doctorName || opt.label}
            />
          </Col>
          <Col lg={4}>
            <AsyncSelect
              loadOptions={formatLoadOptions(lookupTherapeuticAreas)}
              name="therapeuticAreaId"
              control={control}
              label="Therapeutic Area"
              placeholder="Select therapeutic area..."
              getOptionValue={(opt) => opt.therapeuticAreaId || opt.value}
              getOptionLabel={(opt) => opt.therapeuticAreaName || opt.label}
            />
          </Col>
          <Col lg={4}>
            <AsyncSelect
              loadOptions={formatLoadOptions(lookupHospitals)}
              name="hospitalId"
              control={control}
              label="Hospital"
              rules={{ required: "Hospital is required." }}
              placeholder="Select hospital..."
              getOptionValue={(opt) => opt.hospitalId || opt.value}
              getOptionLabel={(opt) => opt.hospitalName || opt.label}
            />
          </Col>
          <Col lg={4}>
            <AsyncSelect
              loadOptions={formatLoadOptions(lookupDiagnoses)}
              name="diagnosisId"
              control={control}
              label="Diagnosis"
              rules={{ required: "Diagnosis is required." }}
              placeholder="Select diagnosis..."
              getOptionValue={(opt) => opt.diagnosisId || opt.value}
              getOptionLabel={(opt) => opt.diagnosisName || opt.label}
            />
          </Col>
          <Col lg={4}>
            <InputField
              name="treatmentLine"
              control={control}
              label="Treatment Line"
              type="text"
              maxLength={100}
              placeholder="Enter treatment line..."
            />
          </Col>
          <Col lg={4}>
            <AsyncSelect
              loadOptions={formatLoadOptions(lookupPatientSegments)}
              name="patientSegmentId"
              control={control}
              label="Patient Segment"
              rules={{ required: "Patient segment is required." }}
              placeholder="Select patient segment..."
              getOptionValue={(opt) => opt.patientSegmentId || opt.value}
              getOptionLabel={(opt) => opt.patientSegmentName || opt.label}
            />
          </Col>
        </Row>
      </Section>

      <Section title="Remarks">
        <Row>
          <Col lg={12}>
            <InputField
              name="remarks"
              control={control}
              type="textarea"
              rows={3}
              maxLength={500}
              showCharCounter
              placeholder="Enter remarks..."
            />
          </Col>
        </Row>
      </Section>
    </Form>
  );
}

function TransactionDetailsTab({ data, editing, registerSubmit, onSetSaving, onSaveSuccess }) {
  if (editing) {
    return <RequestDetailsEditMode data={data} registerSubmit={registerSubmit} onSetSaving={onSetSaving} onSaveSuccess={onSaveSuccess} />;
  }

  return <RequestDetailsViewMode data={data} />;
}

export default function ViewDetails({ show, onCloseClick, data, parentKey, onUpdateParentKey }) {
  if (!show) return null;

  const { state, customFunction } = useCustomHook({
    tabRefreshKey: 0,
  });

  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showMissingEmail, setShowMissingEmail] = useState(false);
  const [editing, setEditing] = useState(false);
  const [isEditSaving, setIsEditSaving] = useState(false);
  const editSubmitRef = useRef(null);

  const handleSendEmailClick = () => {
    if (state.data?.emailAddress) {
      setShowSendEmail(true);
    } else {
      setShowMissingEmail(true);
    }
  };

  const { data: transactionData, refetch } = useFindPatientEnrollmentsQuery(
    { patientEnrollmentId: data?.patientEnrollmentId },
    { skip: !data?.patientEnrollmentId, refetchOnMountOrArgChange: show },
  );
  const findData = transactionData?.returnData;

  const { reset, control } = useForm({ defaultValues: {} });

  useEffect(() => {
    reset({
      ...data,
      ...findData,
    });
    customFunction.updateState({
      data: {
        ...data,
        ...findData,
      },
    });
  }, [show, data, findData, refetch, reset]);

  const handleActions = (bool, data) => {
    if (bool) {
      refetch();
      customFunction.updateToggle("toggleAction");
      customFunction.updateState({
        tabRefreshKey: state?.tabRefreshKey + 1,
      });
      onUpdateParentKey(parentKey + 1);
    } else {
      customFunction.updateToggle("toggleAction", { ...findData, actionName: data });
    }
  };

  const handleEditSaveSuccess = () => {
    setEditing(false);
    refetch();
    customFunction.updateState({
      tabRefreshKey: state?.tabRefreshKey + 1,
    });
    onUpdateParentKey(parentKey + 1);
  };

  const TABS = useMemo(
    () => [
      {
        key: "requestInfo",
        label: "Request Details",
        icon: "ri-user-line",
        component: () => (
          <TransactionDetailsTab
            data={state.data}
            editing={editing}
            registerSubmit={(fn) => (editSubmitRef.current = fn)}
            onSetSaving={setIsEditSaving}
            onSaveSuccess={handleEditSaveSuccess}
          />
        ),
      },
      {
        key: "activities", label: "Activities", icon: "ri-shield-flash-line", component: () =>
          <HistoryList
            transactionId={data?.patientEnrollmentId}
            useLogsQuery={useGetPatientEnrollmentLogsQuery}
            useExportQuery={useExportPatientEnrollmentLogsMutation}
            idKey="patientEnrollmentId"
            title="Activities"
          />
      },
      {
        key: "files", label: "Files", icon: "ri-folder-line", component: () =>
          <TransactionFilesList
            transactionId={data?.patientEnrollmentId}
            useFilesQuery={useGetFilesPatientEnrollmentsQuery}
            idKey="patientEnrollmentId"
            title="Files"
          />
      },
    ],
    [data?.patientEnrollmentId, state.data, editing],
  );

  const BUTTONS = useMemo(
    () => [
      {
        label: "Edit",
        icon: "ri-edit-line",
        className: "emd-btn-ghost",
        show: state?.data?.actions?.editSubmitted && !editing,
        onClick: () => setEditing(true),
      },
      {
        label: isEditSaving ? "Saving..." : "Save",
        icon: "ri-save-line",
        className: "emd-btn-success",
        show: state?.data?.actions?.editSubmitted && editing,
        onClick: () => !isEditSaving && editSubmitRef.current?.(),
      },
      {
        label: "Cancel Edit",
        icon: "ri-close-line",
        className: "emd-btn-ghost",
        show: state?.data?.actions?.editSubmitted && editing,
        onClick: () => setEditing(false),
      },
      {
        label: "Return",
        icon: "ri-arrow-go-back-line",
        className: "emd-btn-warning",
        show: state?.data?.actions?.return && !editing,
        onClick: () => handleActions(false, "Return"),
      },
      {
        label: "Approve",
        icon: "ri-check-double-line",
        className: "emd-btn-success",
        show: state?.data?.actions?.approve && !editing,
        onClick: () => handleActions(false, "Approve"),
      },
      {
        label: "Reject",
        icon: "ri-thumb-down-line",
        className: "emd-btn-danger",
        show: state?.data?.actions?.reject && !editing,
        onClick: () => handleActions(false, "Reject"),
      },
      {
        label: "Cancel",
        icon: "ri-close-line",
        className: "emd-btn-danger",
        show: state?.data?.actions?.cancel && !editing,
        onClick: () => handleActions(false, "Cancel"),
      },
      {
        label: "Send Email",
        icon: "ri-mail-send-line",
        className: "emd-btn-primary",
        show:
          !editing &&
          hasWriteAccess([
            GLOBO_ACCESS_RIGHTS.GLOBO_TRANSACTIONS_PATIENTENROLLMENTS_REQUESTOR,
            GLOBO_ACCESS_RIGHTS.GLOBO_TRANSACTIONS_PATIENTENROLLMENTS_APPROVER,
          ]),
        onClick: () => handleSendEmailClick(),
      },
    ],
    [state?.data?.actions, editing, isEditSaving],
  );

  return (
    <React.Fragment>
      <ActionModal
        state={state}
        customFunction={customFunction}
        patientEnrollmentId={findData?.patientEnrollmentId}
        data={state?.trxValue}
        show={state.toggle.toggleAction}
        onCloseClick={handleActions}
      />

      <TransactionDetailsModal
        title={"Patient Enrollment Request"}
        show={show}
        data={state.data}
        tabs={TABS}
        buttons={BUTTONS}
        onCloseClick={onCloseClick}
        tabRefreshKey={state.tabRefreshKey}
      />

      <SendEmailModal
        show={showSendEmail}
        data={state.data}
        onCloseClick={() => setShowSendEmail(false)}
      />

      <MissingEmailModal
        show={showMissingEmail}
        onCloseClick={() => setShowMissingEmail(false)}
      />
    </React.Fragment>
  );
};
